import { useState } from 'react'
import { Modal } from '../../../ui/Modal'
import { SettingsCheck, SettingsRadio } from '../../../ui/SettingsParts'
import type { Cy } from './draw'
import {
  defaultTitle,
  exportAdmMap,
  exportFileName,
  type ExportArea,
  type ExportBackground,
  type ExportFormat,
  type ExportScale,
  type Paper,
  type PulseControl,
} from './exportMap'

/**
 * Butonul „Export" de pe harta ADM si fereastra lui: PNG sau PDF, zona vazuta
 * sau harta intreaga, fundal, rezolutie, titlu, legenda si adresele IP.
 *
 * Totul se face in browser (`exportMap.ts`) - nimic nu pleaca la server.
 * Fereastra nu stie nimic de asezare, grupare sau vederi: primeste instanta
 * cytoscape asa cum e desenata si o fotografiaza.
 */
export function MapExportButton({
  getCy,
  disabled,
  organization,
  view,
  subtitle,
  partial,
  pulse,
}: {
  getCy: () => Cy | null
  disabled: boolean
  organization: string
  view: string
  /** Ce se vede, in cuvinte - citit abia la deschidere, ca sa fie la zi. */
  subtitle?: () => string
  partial?: boolean
  pulse?: PulseControl
}) {
  const [open, setOpen] = useState(false)
  const [format, setFormat] = useState<ExportFormat>('png')
  const [area, setArea] = useState<ExportArea>('full')
  const [background, setBackground] = useState<ExportBackground>('white')
  const [scale, setScale] = useState<ExportScale>(2)
  const [paper, setPaper] = useState<Paper>('a4')
  const [title, setTitle] = useState('')
  const [legend, setLegend] = useState(true)
  const [includeIps, setIncludeIps] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const today = new Date()

  const show = () => {
    /* Titlul implicit se compune la fiecare deschidere: vederea sau ziua s-au
       putut schimba de la ultima. */
    setTitle(defaultTitle(organization, view, new Date()))
    setError('')
    setOpen(true)
  }

  const run = async () => {
    const cy = getCy()
    if (!cy) {
      setError('The map has not finished drawing yet.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await exportAdmMap(
        cy,
        { format, area, background: format === 'pdf' ? 'white' : background, scale, paper, title, legend, includeIps },
        { organization, view, subtitle: subtitle?.(), partial },
        pulse,
      )
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The export failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        className="btn btn-default btn-sm adm-redraw"
        id="mapExport"
        type="button"
        title="Export the map as a PNG image or a PDF document"
        disabled={disabled}
        onClick={show}
      >
        <i className="fas fa-download"></i> Export
      </button>

      <Modal
        id="mapExportDialog"
        labelledBy="mapExportTitle"
        open={open}
        portal
        onClose={() => (busy ? undefined : setOpen(false))}
        title="Export map"
        footer={
          <>
            <span className="pull-left text-muted" id="mapExportName" style={{ lineHeight: '30px' }}>
              {exportFileName({ organization, view }, format, today)}
            </span>
            <button type="button" className="btn btn-default" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" id="mapExportRun" disabled={busy} onClick={() => void run()}>
              {busy ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-download"></i>} Export {format.toUpperCase()}
            </button>
          </>
        }
      >
        <div className="form-horizontal" id="mapExportForm">
          <div className="form-group">
            <label className="col-sm-4 control-label">Format</label>
            <div className="col-sm-8">
              <SettingsRadio name="mapExportFormat" id="mapExportPng" value="png" checked={format === 'png'} onChange={() => setFormat('png')} label="PNG image" />{' '}
              <SettingsRadio name="mapExportFormat" id="mapExportPdf" value="pdf" checked={format === 'pdf'} onChange={() => setFormat('pdf')} label="PDF document" />
            </div>
          </div>

          <div className="form-group">
            <label className="col-sm-4 control-label">What to export</label>
            <div className="col-sm-8">
              <SettingsRadio name="mapExportArea" id="mapExportFull" value="full" checked={area === 'full'} onChange={() => setArea('full')} label="Whole map" />{' '}
              <SettingsRadio name="mapExportArea" id="mapExportVisible" value="visible" checked={area === 'visible'} onChange={() => setArea('visible')} label="Visible area" />
            </div>
          </div>

          {format === 'png' ? (
            <>
              <div className="form-group">
                <label className="col-sm-4 control-label">Background</label>
                <div className="col-sm-8">
                  <SettingsRadio name="mapExportBg" id="mapExportWhite" value="white" checked={background === 'white'} onChange={() => setBackground('white')} label="White (for printing)" />{' '}
                  <SettingsRadio
                    name="mapExportBg"
                    id="mapExportTransparent"
                    value="transparent"
                    checked={background === 'transparent'}
                    onChange={() => setBackground('transparent')}
                    label="Transparent"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor="mapExportScale">
                  Resolution
                </label>
                <div className="col-sm-8">
                  <select className="form-control input-sm" id="mapExportScale" value={String(scale)} onChange={(e) => setScale(Number(e.target.value) as ExportScale)}>
                    <option value="1">1x (screen)</option>
                    <option value="2">2x (sharp)</option>
                    <option value="3">3x (large prints)</option>
                  </select>
                </div>
              </div>
            </>
          ) : (
            <div className="form-group">
              <label className="col-sm-4 control-label" htmlFor="mapExportPaper">
                Paper
              </label>
              <div className="col-sm-8">
                <select className="form-control input-sm" id="mapExportPaper" value={paper} onChange={(e) => setPaper(e.target.value as Paper)}>
                  <option value="a4">A4</option>
                  <option value="a3">A3</option>
                </select>
                <span className="help-block">Portrait or landscape is chosen from the shape of the map.</span>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="col-sm-4 control-label" htmlFor="mapExportTitleText">
              Title
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control input-sm"
                id="mapExportTitleText"
                value={title}
                placeholder="No title"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <div className="col-sm-offset-4 col-sm-8">
              <SettingsCheck id="mapExportLegend" checked={legend} onChange={setLegend} label="Include the legend" />
              <br />
              <SettingsCheck
                id="mapExportIps"
                checked={includeIps}
                onChange={setIncludeIps}
                label="Include IP addresses"
                title="When unticked, IP addresses are removed from the labels in the exported file"
              />
            </div>
          </div>

          <p className="text-muted small" style={{ marginBottom: 0 }}>
            The file is made in your browser; nothing is sent to the server.
          </p>

          {error !== '' ? (
            <div className="alert alert-warning" id="mapExportError" style={{ marginTop: 10, marginBottom: 0 }}>
              <i className="fas fa-exclamation-triangle"></i> {error}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  )
}
